import React, { useState, useEffect } from 'react';
import { UnderwritingInputs, UnderwritingMetrics, SensitivityMatrix, StudioSession, UserSavedScenario } from '../../types';
import { runUnderwritingSimulation } from '../../lib/api';
import { canSaveScenarios } from '../../config/accessTiers';
import {
  Sliders,
  DollarSign,
  TrendingUp,
  Bookmark,
  Download,
  Lock,
  Layers,
  CheckCircle2,
} from 'lucide-react';

interface UnderwritingSandboxProps {
  initialInputs?: Partial<UnderwritingInputs>;
  canonicalProjectId?: string;
  projectName?: string;
  session: StudioSession;
  onSaveScenario: (payload: { canonical_project_id: string; scenario_name: string; inputs: UnderwritingInputs }) => Promise<void>;
  onExportIcMemo: (canonicalId: string, scenario?: UserSavedScenario | null) => void;
  onOpenAuthModal: () => void;
}

export const UnderwritingSandbox: React.FC<UnderwritingSandboxProps> = ({
  initialInputs,
  canonicalProjectId = 'AXP-US-OH-00154',
  projectName = 'PORTS Technology Campus',
  session,
  onSaveScenario,
  onExportIcMemo,
  onOpenAuthModal,
}) => {
  const [inputs, setInputs] = useState<UnderwritingInputs>({
    it_load_mw: initialInputs?.it_load_mw || 250,
    rent_rate_usd_kw_month: initialInputs?.rent_rate_usd_kw_month || 185,
    capex_per_mw_usd: initialInputs?.capex_per_mw_usd || 8_500_000,
    debt_ltv_pct: initialInputs?.debt_ltv_pct || 65,
    interest_rate_pct: initialInputs?.interest_rate_pct || 7.5,
    opex_pue_reserve_pct: initialInputs?.opex_pue_reserve_pct || 5.0,
    exit_cap_rate_pct: initialInputs?.exit_cap_rate_pct || 6.0,
    user_notes: initialInputs?.user_notes || '',
  });

  const [metrics, setMetrics] = useState<UnderwritingMetrics | null>(null);
  const [sensitivity, setSensitivity] = useState<SensitivityMatrix | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [scenarioName, setScenarioName] = useState('Baseline Institutional Underwrite');
  const [isSaving, setIsSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  useEffect(() => {
    if (initialInputs?.it_load_mw || initialInputs?.rent_rate_usd_kw_month) {
      setInputs((prev) => ({
        ...prev,
        it_load_mw: initialInputs.it_load_mw || prev.it_load_mw,
        rent_rate_usd_kw_month: initialInputs.rent_rate_usd_kw_month || prev.rent_rate_usd_kw_month,
      }));
    }
  }, [initialInputs]);

  useEffect(() => {
    let active = true;
    const calculate = async () => {
      setIsSimulating(true);
      try {
        const res = await runUnderwritingSimulation(inputs);
        if (active) {
          setMetrics(res.metrics);
          setSensitivity(res.sensitivity);
        }
      } catch (err) {
        console.error('Simulation error:', err);
      } finally {
        if (active) setIsSimulating(false);
      }
    };
    calculate();
    return () => {
      active = false;
    };
  }, [inputs]);

  const handleInputChange = (field: keyof UnderwritingInputs, value: number | string) => {
    setInputs((prev) => ({
      ...prev,
      [field]: typeof value === 'number' ? Number(value) : value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveScenario({
        canonical_project_id: canonicalProjectId,
        scenario_name: scenarioName,
        inputs,
      });
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const savePrivilege = canSaveScenarios(session.tier);

  return (
    <div className="flex flex-col h-full bg-[#06090e] overflow-y-auto p-4 space-y-4 select-none">
      {/* 1. Header */}
      <div className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <div className="flex items-center space-x-2">
            <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-[#2979ff]/15 text-[#2979ff] border border-[#2979ff]/30 font-bold">
              UNDERWRITING SANDBOX
            </span>
            <span className="text-xs text-slate-400 font-mono">
              REAL-TIME DCF &bull; YIELD ON COST &bull; 5-YR LEVERED IRR &bull; SENSITIVITY
            </span>
          </div>
          <h2 className="text-lg font-bold text-white mt-1">
            Institutional Deal Underwriting Sandbox
          </h2>
          <p className="text-xs text-slate-400">
            Active Asset: <b className="text-slate-200">{projectName}</b> ({canonicalProjectId})
          </p>
        </div>

        <div className="flex items-center space-x-2">
          {savePrivilege.allowed ? (
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#2979ff] hover:bg-[#1c68e8] text-white font-semibold text-xs transition disabled:opacity-50"
            >
              {savedSuccess ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
              <span>{isSaving ? 'Saving...' : savedSuccess ? 'Saved to Workspace!' : 'Save Scenario'}</span>
            </button>
          ) : (
            <button
              onClick={onOpenAuthModal}
              className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#121a2d] text-slate-300 hover:text-white border border-[#1a253b] text-xs font-semibold"
            >
              <Lock className="w-3.5 h-3.5 text-amber-400" />
              <span>Sign in to Save</span>
            </button>
          )}

          <button
            onClick={() => {
              const liveScenario: UserSavedScenario | null = metrics
                ? {
                    scenario_id: 'live-sandbox-scenario',
                    canonical_project_id: canonicalProjectId,
                    scenario_name: scenarioName || `${projectName} (Live Underwriting)`,
                    it_load_mw: inputs.it_load_mw,
                    rent_rate_usd_kw_month: inputs.rent_rate_usd_kw_month,
                    capex_per_mw_usd: inputs.capex_per_mw_usd,
                    debt_ltv_pct: inputs.debt_ltv_pct,
                    interest_rate_pct: inputs.interest_rate_pct,
                    total_capex_usd: metrics.total_capex_usd,
                    annual_noi_usd: metrics.net_operating_income_usd,
                    unlevered_yield_on_cost_pct: metrics.unlevered_yield_on_cost_pct,
                    levered_irr_5yr_pct: metrics.levered_irr_5yr_pct,
                    user_notes: inputs.user_notes,
                    updated_at: new Date().toISOString(),
                  }
                : null;
              onExportIcMemo(canonicalProjectId, liveScenario);
            }}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg bg-[#121a2d] hover:bg-[#1a253b] text-slate-200 border border-[#1a253b] text-xs font-semibold transition"
          >
            <Download className="w-3.5 h-3.5 text-[#2979ff]" />
            <span>Generate IC Memo (PDF)</span>
          </button>
        </div>
      </div>

      {/* 2. Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Column: Interactive Inputs (5 cols) */}
        <div className="lg:col-span-5 p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#2979ff] flex items-center">
            <Sliders className="w-3.5 h-3.5 mr-1.5" />
            Financial Model Assumptions
          </h3>

          {/* Scenario Name */}
          <div>
            <label className="text-[11px] uppercase font-mono text-slate-400 block mb-1">
              Scenario Name
            </label>
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#2979ff]"
            />
          </div>

          {/* IT Load */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-slate-300">Contracted IT Load:</span>
              <span className="text-[#2979ff] font-bold">{inputs.it_load_mw.toLocaleString()} MW</span>
            </div>
            <input
              type="range"
              min="20"
              max="2000"
              step="10"
              value={inputs.it_load_mw}
              onChange={(e) => handleInputChange('it_load_mw', Number(e.target.value))}
              className="w-full accent-[#2979ff]"
            />
            <div className="flex justify-between text-[10px] text-slate-500 font-mono">
              <span>20 MW</span>
              <span>1,000 MW</span>
              <span>2,000 MW</span>
            </div>
          </div>

          {/* Rent Rate */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-slate-300">Triple-Net Rent Rate:</span>
              <span className="text-emerald-400 font-bold">${inputs.rent_rate_usd_kw_month}/kW-mo</span>
            </div>
            <input
              type="range"
              min="120"
              max="320"
              step="5"
              value={inputs.rent_rate_usd_kw_month}
              onChange={(e) => handleInputChange('rent_rate_usd_kw_month', Number(e.target.value))}
              className="w-full accent-emerald-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500 font-mono">
              <span>$120 (Rural)</span>
              <span>$185 (Benchmark)</span>
              <span>$320 (Ashburn)</span>
            </div>
          </div>

          {/* Turnkey Capex */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-slate-300">Turnkey Capex:</span>
              <span className="text-white font-bold">${(inputs.capex_per_mw_usd / 1_000_000).toFixed(2)}M/MW</span>
            </div>
            <input
              type="range"
              min="6000000"
              max="14000000"
              step="250000"
              value={inputs.capex_per_mw_usd}
              onChange={(e) => handleInputChange('capex_per_mw_usd', Number(e.target.value))}
              className="w-full accent-[#2979ff]"
            />
            <div className="flex justify-between text-[10px] text-slate-500 font-mono">
              <span>$6.0M/MW</span>
              <span>$8.5M (Tier 1 AI)</span>
              <span>$14.0M/MW</span>
            </div>
          </div>

          {/* Debt LTV */}
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-slate-300">Debt LTV (%):</span>
              <span className="text-cyan-400 font-bold">{inputs.debt_ltv_pct}%</span>
            </div>
            <input
              type="range"
              min="0"
              max="80"
              step="5"
              value={inputs.debt_ltv_pct}
              onChange={(e) => handleInputChange('debt_ltv_pct', Number(e.target.value))}
              className="w-full accent-cyan-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500 font-mono">
              <span>0% (All Equity)</span>
              <span>65% (Institutional)</span>
              <span>80%</span>
            </div>
          </div>

          {/* Secondary Inputs */}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#1a253b]">
            <div>
              <label className="text-[10px] font-mono text-slate-400 block mb-1">Interest %</label>
              <input
                type="number"
                step="0.25"
                value={inputs.interest_rate_pct}
                onChange={(e) => handleInputChange('interest_rate_pct', Number(e.target.value))}
                className="w-full bg-[#06090e] border border-[#1a253b] rounded px-2 py-1 text-xs font-mono text-white"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono text-slate-400 block mb-1">Reserve %</label>
              <input
                type="number"
                step="0.5"
                value={inputs.opex_pue_reserve_pct}
                onChange={(e) => handleInputChange('opex_pue_reserve_pct', Number(e.target.value))}
                className="w-full bg-[#06090e] border border-[#1a253b] rounded px-2 py-1 text-xs font-mono text-white"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono text-slate-400 block mb-1">Exit Cap %</label>
              <input
                type="number"
                step="0.25"
                value={inputs.exit_cap_rate_pct}
                onChange={(e) => handleInputChange('exit_cap_rate_pct', Number(e.target.value))}
                className="w-full bg-[#06090e] border border-[#1a253b] rounded px-2 py-1 text-xs font-mono text-white"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase font-mono text-slate-400 block mb-1">
              Private Diligence Notes
            </label>
            <textarea
              rows={2}
              value={inputs.user_notes}
              onChange={(e) => handleInputChange('user_notes', e.target.value)}
              placeholder="Underwriting memo notes, transmission upgrade reserve adjustments..."
              className="w-full bg-[#06090e] border border-[#1a253b] rounded-lg p-2 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-[#2979ff]"
            />
          </div>
        </div>

        {/* Right Column: Live Returns & Sensitivity (7 cols) */}
        <div className="lg:col-span-7 space-y-4">
          {/* Key Output 4-Card Ribbon */}
          {metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">Total Capex</div>
                <div className="text-base font-bold font-mono text-white">
                  ${(metrics.total_capex_usd / 1_000_000).toFixed(1)}M
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Equity: ${(metrics.equity_amount_usd / 1_000_000).toFixed(1)}M
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">Annual Net NOI</div>
                <div className="text-base font-bold font-mono text-emerald-400">
                  ${(metrics.net_operating_income_usd / 1_000_000).toFixed(1)}M
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  Gross: ${(metrics.gross_annual_rev_usd / 1_000_000).toFixed(1)}M
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">Yield on Cost</div>
                <div className="text-base font-bold font-mono text-amber-400">
                  {metrics.unlevered_yield_on_cost_pct.toFixed(2)}%
                </div>
                <div className="text-[10px] text-slate-500 font-mono">Unlevered Return</div>
              </div>

              <div className="p-3 rounded-lg bg-[#0c121e] border border-[#1a253b]">
                <div className="text-[10px] uppercase font-mono text-slate-400">5-Yr Levered IRR</div>
                <div className="text-base font-bold font-mono text-cyan-400">
                  {metrics.levered_irr_5yr_pct.toFixed(1)}%
                </div>
                <div className="text-[10px] text-slate-500 font-mono">
                  CoC: {metrics.cash_on_cash_pct.toFixed(1)}%
                </div>
              </div>
            </div>
          )}

          {/* Cash Flow Statement */}
          {metrics && (
            <div className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] space-y-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">
                Underwriting Cash Flow Statement
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono">
                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <span className="text-slate-500 text-[10px] block">Debt Facility (LTV)</span>
                  <span className="text-slate-200 font-bold">${(metrics.debt_amount_usd / 1_000_000).toFixed(1)}M</span>
                </div>
                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <span className="text-slate-500 text-[10px] block">Annual Debt Service</span>
                  <span className="text-rose-400 font-bold">-${(metrics.annual_debt_service_usd / 1_000_000).toFixed(2)}M</span>
                </div>
                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <span className="text-slate-500 text-[10px] block">Levered Free Cash Flow</span>
                  <span className="text-emerald-400 font-bold">${(metrics.levered_cash_flow_usd / 1_000_000).toFixed(2)}M</span>
                </div>
                <div className="p-2.5 rounded bg-[#06090e] border border-[#1a253b]">
                  <span className="text-slate-500 text-[10px] block">Terminal Exit Value</span>
                  <span className="text-amber-400 font-bold">${(metrics.terminal_val_usd / 1_000_000).toFixed(1)}M</span>
                </div>
              </div>
            </div>
          )}

          {/* 5x5 Sensitivity Matrix */}
          {sensitivity && (
            <div className="p-4 rounded-xl bg-[#0c121e] border border-[#1a253b] space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#2979ff] flex items-center">
                  <Layers className="w-3.5 h-3.5 mr-1.5" />
                  5x5 Sensitivity Matrix: 5-Year Levered IRR vs Turnkey Capex &amp; Rent
                </h4>
                <span className="text-[10px] font-mono text-slate-500">
                  Rows: Rent ($/kW-mo) &bull; Cols: Capex ($/MW)
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono text-center">
                  <thead>
                    <tr className="border-b border-[#1a253b] text-[10px] text-slate-400">
                      <th className="p-1.5 text-left text-amber-400">Rent \ Capex</th>
                      {sensitivity.capex_steps.map((c, i) => (
                        <th key={i} className="p-1.5">
                          ${(c / 1_000_000).toFixed(2)}M
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sensitivity.matrix.map((row, rIdx) => (
                      <tr key={rIdx} className="border-b border-[#1a253b]/60 hover:bg-[#121a2d]">
                        <td className="p-1.5 text-left font-bold text-slate-300">
                          ${sensitivity.rent_steps[rIdx]}/kW
                        </td>
                        {row.map((cell, cIdx) => {
                          const isHigh = cell.irr >= 25.0;
                          const isMedium = cell.irr >= 15.0 && cell.irr < 25.0;
                          return (
                            <td
                              key={cIdx}
                              className={`p-1.5 font-bold ${
                                isHigh
                                  ? 'text-emerald-400 bg-emerald-950/30'
                                  : isMedium
                                  ? 'text-amber-400 bg-amber-950/20'
                                  : 'text-slate-400'
                              }`}
                            >
                              {cell.irr.toFixed(1)}%
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
